/*!
 * Copyright © 2023. Verizon Connect Ireland Limited. All rights reserved.
 */

import { Directive, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { GoogleMapsShapeUtils } from '../../../../utils/google-maps-shapes.utils';
import { LatLng } from '../../../core/models';
import { EGeofenceDrawingControlShape } from '../../../geofence/models';
import { GoogleMapsComponent } from '../../components';
import { GoogleMapsShape } from '../../models';

@Directive({
  selector: '[fmGoogleMapsGeofenceDrawing]'
})
export class GoogleMapsGeofenceDrawingDirective implements OnChanges, OnInit {
  @Input() public isGeofenceCreateOpen: boolean;
  @Input() public geofenceDrawingShape: EGeofenceDrawingControlShape;
  @Input() public geofenceShapeLatLng: LatLng;
  @Input() public clearShape: Date;
  @Input() public geofenceShape: GoogleMapsShape;

  @Output()
  public geofenceShapeChanged = new EventEmitter<GoogleMapsShape>();

  private _existingGeofenceShape: GoogleMapsShape | null = null;
  private _drawingManager: google.maps.drawing.DrawingManager;
  private readonly _defaultGeofenceRadiusMeters = 160.934; // 0.1 miles
  private readonly _defaultShapeOptions = {
    editable: true,
    draggable: true,
    fillColor: '#0077B4',
    fillOpacity: 0.32,
    strokeColor: '#000000',
    strokeOpacity: 1,
    zIndex: 1000,
    strokeWeight: 2,
    geodesic: true
  };
  private _shapeListeners: google.maps.MapsEventListener[] = [];

  constructor(private readonly _googleMapsHost: GoogleMapsComponent) {}

  ngOnInit(): void {
    this._googleMapsHost.mapReady$.subscribe(() => {
      this._initDrawingManager();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (typeof google === 'object' && typeof google.maps === 'object') {
      if (this.isGeofenceCreateOpen === false) {
        this._clearCurrentGeofenceShape();
        return;
      }

      if (changes.clearShape) {
        this._clearCurrentGeofenceShape();
        return;
      }
      if (changes.geofenceShape) {
        if (this.geofenceShape != null) {
          this._updateExistingShape(this.geofenceShape);
          this._googleMapsHost.map.fitBounds(GoogleMapsShapeUtils.getBoundsFromShape(this._existingGeofenceShape), 200);
        } else if (this.geofenceShapeLatLng != null) {
          this._drawNewDefaultShape(this.geofenceDrawingShape, this.geofenceShapeLatLng);
        }
        return;
      }
      if (
        changes.geofenceShapeLatLng &&
        this._existingGeofenceShape &&
        !this._latLngMatchesShapeCenter(this.geofenceShapeLatLng, this._existingGeofenceShape)
      ) {
        this._updateExistingShapeLatLng(this.geofenceShapeLatLng);
        return;
      }

      if (changes.geofenceDrawingShape && this._existingGeofenceShape) {
        this._updateExistingShapeType(this.geofenceDrawingShape);
        return;
      }

      if (this.geofenceShapeLatLng && !this._existingGeofenceShape) {
        this._drawNewDefaultShape(this.geofenceDrawingShape, this.geofenceShapeLatLng);
        return;
      }

      if (!this._existingGeofenceShape) {
        this._newShapeWithDrawingTool(this.geofenceDrawingShape);
        return;
      }
    }
  }

  private _latLngMatchesShapeCenter(latLng: LatLng, shape: GoogleMapsShape) {
    const existingLatLng = GoogleMapsShapeUtils.getBoundsFromShape(shape).getCenter();
    return existingLatLng.lat() === latLng.lat && existingLatLng.lng() === latLng.lng;
  }

  private _updateExistingShapeLatLng(latLng: LatLng) {
    const movedShape = GoogleMapsShapeUtils.moveShape(this._existingGeofenceShape, latLng);
    this._updateExistingShape(movedShape);
  }

  private _updateExistingShapeType(shapeType: EGeofenceDrawingControlShape) {
    if (shapeType === EGeofenceDrawingControlShape.Circle) {
      this._updateExistingShapeToCircle();
    } else if (shapeType === EGeofenceDrawingControlShape.Rectangle) {
      this._updateExistingShapeToRectangle();
    } else {
      this._newShapeWithDrawingTool(EGeofenceDrawingControlShape.Polygon);
    }
  }

  private _updateExistingShapeToCircle() {
    const existingBounds = GoogleMapsShapeUtils.getBoundsFromShape(this._existingGeofenceShape);
    const radius = GoogleMapsShapeUtils.getRadiusFromBounds(existingBounds);
    const newShape = new google.maps.Circle({
      ...GoogleMapsShapeUtils.getBaseOptions(this._existingGeofenceShape),
      center: existingBounds.getCenter(),
      radius: radius
    });
    this._updateExistingShape(newShape);
  }

  private _updateExistingShapeToRectangle() {
    const existingBounds = GoogleMapsShapeUtils.getBoundsFromShape(this._existingGeofenceShape);
    const newShape = new google.maps.Rectangle({
      ...GoogleMapsShapeUtils.getBaseOptions(this._existingGeofenceShape),
      bounds: existingBounds
    });
    this._updateExistingShape(newShape);
  }

  private _initDrawingManager(): void {
    const baseShapeOptions = {
      ...this._defaultShapeOptions,
      map: this._googleMapsHost.map
    };
    this._drawingManager = new google.maps.drawing.DrawingManager({
      drawingControl: false,
      polygonOptions: {
        ...baseShapeOptions
      },
      circleOptions: {
        ...baseShapeOptions
      },
      rectangleOptions: {
        ...baseShapeOptions
      },
      map: this._googleMapsHost.map
    });
  }

  private _drawNewDefaultShape(geofenceDrawingShape: EGeofenceDrawingControlShape, geofenceShapeLatLng: LatLng) {
    switch (geofenceDrawingShape) {
      case EGeofenceDrawingControlShape.Circle:
        this._drawNewDefaultCircle(geofenceShapeLatLng);
        break;
      case EGeofenceDrawingControlShape.Rectangle:
        this._drawNewDefaultRectangle(geofenceShapeLatLng);
        break;
      case EGeofenceDrawingControlShape.Polygon:
        this._newShapeWithDrawingTool(EGeofenceDrawingControlShape.Polygon);
        break;
    }
  }

  private _drawNewDefaultCircle(latLng: LatLng) {
    const circle = new google.maps.Circle({
      ...this._defaultShapeOptions,
      radius: this._defaultGeofenceRadiusMeters,
      center: new google.maps.LatLng(latLng.lat, latLng.lng)
    });

    this._updateExistingShape(circle);
    this._googleMapsHost.map.fitBounds(GoogleMapsShapeUtils.getBoundsFromShape(this._existingGeofenceShape), 200);
  }

  private _drawNewDefaultRectangle(latLng: LatLng): void {
    const rectangle = new google.maps.Rectangle({
      ...this._defaultShapeOptions,
      bounds: this._getDefaultBoundsAtLatLng(latLng)
    });

    this._updateExistingShape(rectangle);
    this._googleMapsHost.map.fitBounds(GoogleMapsShapeUtils.getBoundsFromShape(this._existingGeofenceShape), 200);
  }

  private _newShapeWithDrawingTool(drawingControlShape: EGeofenceDrawingControlShape): void {
    this._clearCurrentGeofenceShape();
    const overlayType = this._getOverlayTypeFromDrawingControlShape(drawingControlShape);
    this._drawingManager.setDrawingMode(overlayType);
    this._drawingManager.addListener('overlaycomplete', event => {
      this._drawingManager.setDrawingMode(null);
      this._updateExistingShape(event.overlay);
    });
  }

  private _clearCurrentGeofenceShape(): void {
    this._drawingManager.setDrawingMode(null);
    this._existingGeofenceShape?.setMap(null);
    this._existingGeofenceShape = null;
    this.geofenceShapeChanged.emit(null);
  }

  private _getDefaultBoundsAtLatLng(latLng: LatLng): google.maps.LatLngBounds {
    const circle = new google.maps.Circle({
      center: new google.maps.LatLng(latLng.lat, latLng.lng),
      radius: this._defaultGeofenceRadiusMeters
    });
    return GoogleMapsShapeUtils.getBoundsFromShape(circle);
  }

  private _getOverlayTypeFromDrawingControlShape(drawingControlShape: EGeofenceDrawingControlShape): google.maps.drawing.OverlayType {
    return drawingControlShape === EGeofenceDrawingControlShape.Circle
      ? google.maps.drawing.OverlayType.CIRCLE
      : drawingControlShape === EGeofenceDrawingControlShape.Rectangle
      ? google.maps.drawing.OverlayType.RECTANGLE
      : google.maps.drawing.OverlayType.POLYGON;
  }

  private _updateExistingShape(newShape: GoogleMapsShape) {
    this._removeExistingShape();
    this._setExistingShape(newShape);
    this._setupBoundsChangedListener(newShape);
  }

  private _setupBoundsChangedListener(newShape: GoogleMapsShape) {
    const events = [];
    const listenObject = newShape instanceof google.maps.Polygon ? newShape.getPath() : this._existingGeofenceShape;
    if (newShape instanceof google.maps.Polygon) {
      events.push('set_at', 'insert_at', 'remove_at');
    } else {
      events.push('bounds_changed');
    }

    events.forEach(eventName => {
      const listener = google.maps.event.addListener(
        listenObject,
        eventName,
        (() => {
          // This timer is used to debounce the bounds_changed events to avoid redrawing the shape
          // while being dragged
          let timer: NodeJS.Timeout;
          return () => {
            clearTimeout(timer);
            timer = setTimeout(() => {
              this._updateExistingShape(newShape);
            }, 250);
          };
        })()
      );
      this._shapeListeners.push(listener);
    });
  }

  private _removeExistingShape() {
    this._existingGeofenceShape?.setMap(null);
    this._existingGeofenceShape = null;
    this._shapeListeners.forEach(listener => {
      google.maps.event.removeListener(listener);
    });
    this._shapeListeners = [];
  }

  private _setExistingShape(newShape: GoogleMapsShape) {
    this._existingGeofenceShape = newShape;
    this._existingGeofenceShape.setMap(this._googleMapsHost.map);
    this.geofenceShapeChanged.emit(this._existingGeofenceShape);
  }
}
